import { Dispatch } from "@reduxjs/toolkit";
import { ClientPasswordLinkResponse, RegisterClientRequest } from "hvp-shared";
import clientBillingApi from "../../api/clientBillingApi";
import hvpPublicApi from "../../api/hvpPublicApi";
import {
  setClientLoading,
  clientLoginSuccess,
  clientLoginFailure,
  clientLogout,
  updateClientProfile,
  clearClientError,
} from "../slice/clientAuthSlice";

const route = "/client-billing";

export const clientCheckAuth = () => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setClientLoading(true));
      const token = localStorage.getItem("clientToken");
      if (!token) {
        return dispatch(setClientLoading(false));
      }
      const { data } = await clientBillingApi.post(`${route}/auth/refresh`);
      if (data.ok) {
        localStorage.setItem("clientToken", data.data.token);
        dispatch(clientLoginSuccess(data.data.client));
      }
    } catch (error: any) {
      // Only clear token if server explicitly rejected it (401)
      // Don't clear on connection errors (server restart, network issue)
      if (error.response?.status === 401) {
        localStorage.removeItem("clientToken");
        dispatch(clientLoginFailure("Sesión expirada. Inicia sesión de nuevo."));
      } else {
        // Connection error — keep token, just stop loading
        dispatch(setClientLoading(false));
      }
    } finally {
      dispatch(setClientLoading(false));
    }
  };
};

export const clientLogin = (rfc: string, password: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setClientLoading(true));
      const { data } = await hvpPublicApi.post(`${route}/auth/login`, { rfc, password });
      if (data.ok) {
        localStorage.setItem("clientToken", data.data.token);
        dispatch(clientLoginSuccess(data.data.client));
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || "Login failed";
      dispatch(clientLoginFailure(message));
    } finally {
      dispatch(setClientLoading(false));
    }
  };
};

export const clientGoogleLogin = (credential: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setClientLoading(true));
      const { data } = await hvpPublicApi.post(`${route}/auth/google`, { credential });
      if (data.ok) {
        localStorage.setItem("clientToken", data.data.token);
        dispatch(clientLoginSuccess(data.data.client));
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || "Google login failed";
      dispatch(clientLoginFailure(message));
    } finally {
      dispatch(setClientLoading(false));
    }
  };
};

/** What register resolves to: a link went out (GH126); no session comes from registering */
export interface ClientRegisterLinkSent {
  linkSent: true;
  message: string;
}

const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error?.message || error?.message || fallback;

/**
 * Register: the server mails a link to create the password — to the email typed for a new RFC, to
 * the email on file for an RFC that already has an account — and answers the same for both.
 * Resolves to the message to show, or undefined after an error (shown through the slice).
 */
export const clientRegister = (registerData: Omit<RegisterClientRequest, "password">) => {
  return async (dispatch: Dispatch): Promise<ClientRegisterLinkSent | undefined> => {
    try {
      dispatch(setClientLoading(true));
      const { data } = await hvpPublicApi.post<{ ok: boolean; data: ClientPasswordLinkResponse }>(
        `${route}/auth/register`,
        registerData
      );
      dispatch(clearClientError());
      return { linkSent: true, message: data.data.message };
    } catch (error: any) {
      dispatch(clientLoginFailure(apiErrorMessage(error, "No se pudo registrar. Intenta de nuevo.")));
      return undefined;
    } finally {
      dispatch(setClientLoading(false));
    }
  };
};

/**
 * Ask for the emailed link to create or reset the password. Resolves to the server's message,
 * which is the same whether or not the RFC has an account.
 */
export const requestClientPasswordLink = async (rfc: string): Promise<string> => {
  try {
    const { data } = await hvpPublicApi.post<{ ok: boolean; data: ClientPasswordLinkResponse }>(
      `${route}/auth/password-link`,
      { rfc }
    );
    return data.data.message;
  } catch (error: any) {
    throw new Error(apiErrorMessage(error, "No se pudo enviar el enlace. Intenta de nuevo."));
  }
};

/** Set the password with the link's token and sign in. Throws with the server's message. */
export const clientSetPassword = (token: string, password: string) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const { data } = await hvpPublicApi.post(`${route}/auth/password`, { token, password });
      localStorage.setItem("clientToken", data.data.token);
      dispatch(clientLoginSuccess(data.data.client));
    } catch (error: any) {
      throw new Error(apiErrorMessage(error, "No se pudo guardar la contraseña. Intenta de nuevo."));
    }
  };
};

export const clientUpdateProfile = (profileData: Record<string, string>) => {
  return async (dispatch: Dispatch) => {
    try {
      const { data } = await clientBillingApi.patch(`${route}/profile`, profileData);
      if (data.ok) {
        dispatch(updateClientProfile(data.data));
      }
      return data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || "Profile update failed");
    }
  };
};

export const clientDoLogout = () => {
  return (dispatch: Dispatch) => {
    localStorage.removeItem("clientToken");
    dispatch(clientLogout());
  };
};
